import apiClient from './api';
import type { OrderPackageSelection, OrderStatus } from '../types/orders.types';

export type ReportedProblemStatus = 'open' | 'resolved';

export type VendorStatus = 'Incomplete' | 'InReview' | 'Active' | 'Suspended';

/**
 * A user-reported problem as returned by GET /admins/reported-problems.
 * Mirrors `ReportedProblem` in eventi-backend/src/services/reportedProblemsService.ts.
 */
export interface ReportedProblem {
  problemId: string;
  userId: string;
  status: ReportedProblemStatus;
  description: string;
  /** Present when the user reported the problem from an order screen. */
  orderId?: string;
  /** Vendor owning `orderId`, denormalized at submission time. */
  vendorId?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

export interface ReportedProblemUserSummary {
  userId: string;
  name: string;
  phone: string;
  email: string;
  imageProfile: string;
}

export interface ReportedProblemVendorSummary {
  vendorId: string;
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  profileImageUrl: string;
  status?: VendorStatus;
}

export interface ReportedProblemOrderSummary {
  orderId: string;
  orderNumber: string;
  status: OrderStatus;
  total: number;
  discountAmount?: number;
  discountType?: string;
  shippingFee?: number;
  createdAt: number;
  updatedAt: number;
  eventDateTime?: string;
  eventAddress?: string;
  eventZone?: string;
  contactName?: string;
  contactPhone?: string;
  specialRequests?: string;
  productId: string;
  productName: string;
  productDetails: string;
  packages?: OrderPackageSelection[];
  hasDispute: boolean;
  disputeReason?: string;
  hasRefund: boolean;
  feeAmount?: number;
  vendorPayoutAmount?: number;
}

/** A reported problem plus the resolved user, order and vendor context. */
export interface EnrichedReportedProblem extends ReportedProblem {
  user: ReportedProblemUserSummary;
  order?: ReportedProblemOrderSummary;
  vendor?: ReportedProblemVendorSummary;
}

export interface ReportedProblemsListResponse {
  items: EnrichedReportedProblem[];
  /** Opaque DynamoDB cursor. Round-trip unchanged to fetch the next page. */
  lastEvaluatedKey?: Record<string, unknown>;
}

export interface ListReportedProblemsParams {
  status?: ReportedProblemStatus;
  limit?: number;
  lastEvaluatedKey?: Record<string, unknown>;
}

/**
 * Admin reported-problems service. Wraps:
 *   GET   /admins/reported-problems
 *   PATCH /admins/reported-problems/{problemId}/resolve
 *
 * apiClient's request interceptor auto-prefixes /admins, so paths omit it here.
 */
export const reportedProblemsService = {
  list: async (
    params: ListReportedProblemsParams = {},
  ): Promise<ReportedProblemsListResponse> => {
    const search = new URLSearchParams();
    if (params.status) search.append('status', params.status);
    if (params.limit !== undefined) search.append('limit', String(params.limit));
    if (params.lastEvaluatedKey) {
      // Backend expects a JSON-encoded DynamoDB key in the query string.
      search.append('lastEvaluatedKey', JSON.stringify(params.lastEvaluatedKey));
    }

    const suffix = search.toString() ? `?${search.toString()}` : '';
    const response = await apiClient.get<{ data: ReportedProblemsListResponse }>(
      `/reported-problems${suffix}`,
    );

    // Handler wraps responses in { success, message, data }.
    return response.data.data;
  },

  resolve: async (problemId: string): Promise<EnrichedReportedProblem> => {
    const response = await apiClient.patch<{ data: { problem: EnrichedReportedProblem } }>(
      `/reported-problems/${problemId}/resolve`,
      {},
    );

    return response.data.data.problem;
  },
};

export default reportedProblemsService;
